
import { Component } from "@theme/component";
import {
  debounce,
  getSectionId,
  removeScrollLockClass,
} from "@theme/utilities";

class PredictiveSearch extends Component {
  #abortController = new AbortController();
  #lastKeydown = null;
  #focusoutTimeout = null;
  #isLoading = false;
  #requestController = null;
  #requestId = 0;

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    this.cachedMap = new Map();

    this.onInputDebounced = debounce(
      this.onChange.bind(this),
      300
    );

    this.header = document.querySelector("header");

    this.escKeyPressHandler =
      this.#handleEscKeyPress.bind(this);

    this.clickOutsideHandler =
      this.#handleClickOutside.bind(this);

    this.focusOutHandler =
      this.#handleFocusOut.bind(this);

    this.addEventListener(
      "focusout",
      this.focusOutHandler,
      { signal }
    );

    this.states = {
      OPEN: "predictive-search-open",
      SEARCH_OPEN: "search-open",
    };

    this.transitionEndHandler = null;
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#requestController?.abort();

    this.#cancelCloseAnimation();

    document.removeEventListener(
      "click",
      this.clickOutsideHandler
    );

    document.removeEventListener(
      "keydown",
      this.escKeyPressHandler
    );
  }

  get isEmptyRecommendations() {
    return this.dataset.emptyRecommendations === "true";
  }

  get isHeaderSearch() {
    return this.dataset.context === "header";
  }

  // Detect the current storefront language.
  get isArabicLocale() {
    const locale =
      window.Shopify?.locale ||
      document.documentElement.lang ||
      "";

    return locale.toLowerCase().startsWith("ar");
  }

  getQuery() {
    return this.refs.searchInput.value.trim();
  }

  onFocus(event) {
    if (
      this.getQuery().length === 0 &&
      this.isEmptyRecommendations
    ) {
      return;
    }

    this.toggleSearchState(true);

    if (this.getQuery().length === 0) {
      this.refs.searchContent?.classList.remove("hidden");
    } else {
      const url = this.setupURL().toString();
      this.renderSection(url);
    }
  }

  #handleEscKeyPress(event) {
    const { key } = event;

    this.#lastKeydown = key;

    if (key === "Escape") {
      this.clear();
    }
  }

  close(event) {
    event?.preventDefault();
    this.#cancelRequest();
    this.toggleSearchState(false);
  }

  clear(event = null) {
    event?.preventDefault();

    this.#cancelRequest();

    this.refs.searchInput.value = "";
    this.refs.searchInput.focus();

    this.removeAttribute("results");
    this.removeAttribute("error");

    const resultsElement = this.querySelector(
      `[id="PredictiveSearchResults-${getSectionId(this)}"]`
    );

    if (resultsElement) {
      resultsElement.innerHTML = "";
    }

    this.toggleSearchState(false);
  }

  #cancelRequest() {
    this.#requestId++;

    if (this.#requestController) {
      this.#requestController.abort();
      this.#requestController = null;
    }

    this.#setLoadingState(false);
  }

  // Arabic uses storefront search.
  // Other languages keep the original predictive API.
  setupURL() {
    const searchTerm = this.getQuery();

    if (this.isArabicLocale) {
      const root =
        window.Shopify?.routes?.root || "/ar/";

      const normalizedRoot = root.endsWith("/")
        ? root
        : `${root}/`;

      const url = new URL(
        `${normalizedRoot}search`,
        window.location.origin
      );

      url.searchParams.set("q", searchTerm);
      url.searchParams.set("type", "product");
      url.searchParams.set("options[prefix]", "last");

      url.searchParams.set(
        "section_id",
        "arabic-search-suggestions"
      );

      return url;
    }

    // Original FoxTheme predictive search.
    const url = new URL(
      `${FoxTheme.routes.shop_url}${FoxTheme.routes.predictive_search_url}`
    );

    url.searchParams.set("q", searchTerm);

    url.searchParams.set(
      "resources[limit]",
      this.dataset.resultsLimit || 3
    );

    url.searchParams.set(
      "resources[limit_scope]",
      "each"
    );

    url.searchParams.set(
      "section_id",
      getSectionId(this)
    );

    return url;
  }

  onSubmit(event) {
    if (this.getQuery().length === 0) {
      event.preventDefault();
      this.refs.searchInput.focus();
    }
  }

  onChange() {
    const query = this.getQuery();

    if (query.length === 0) {
      this.clear();
      return;
    }

    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    // Cancel earlier requests when users type quickly.
    this.#cancelRequest();

    const requestId = this.#requestId;

    if (this.cachedMap.has(url)) {
      this.renderSectionFromCache(url);
    } else {
      this.renderSectionFromFetch(url, requestId);
    }
  }

  renderSectionFromCache(url) {
    const requestedQuery =
      new URL(url).searchParams.get("q");

    if (this.getQuery() !== requestedQuery) {
      return;
    }

    const responseText = this.cachedMap.get(url);

    this.renderSearchResults(responseText);
    this.setAttribute("results", "");
  }

  async renderSectionFromFetch(url, requestId) {
    const controller = new AbortController();
    this.#requestController = controller;

    this.#setLoadingState(true);
    this.removeAttribute("error");

    const requestedQuery =
      new URL(url).searchParams.get("q");

    try {
      const response = await fetch(url, {
        signal: controller.signal,
        credentials: "same-origin",
      });

      if (!response.ok) {
        throw new Error(
          `Search request failed: ${response.status}`
        );
      }

      const responseText = await response.text();

      if (
        controller.signal.aborted ||
        requestId !== this.#requestId ||
        this.getQuery() !== requestedQuery
      ) {
        return;
      }

      // Validate the section before caching it.
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(
        responseText,
        "text/html"
      );

      const expectedElement = this.isArabicLocale
        ? parsedDoc.querySelector(
            "[data-arabic-search-results]"
          )
        : parsedDoc.getElementById(
            `PredictiveSearchResults-${getSectionId(this)}`
          );

      if (!expectedElement) {
        throw new Error(
          "Expected search section was not returned."
        );
      }

      this.cachedMap.set(url, responseText);

      this.#setLoadingState(false);
      this.renderSearchResults(responseText);
    } catch (error) {
      if (
        controller.signal.aborted ||
        requestId !== this.#requestId
      ) {
        return;
      }

      console.error(
        "Error fetching search data:",
        error
      );

      this.setAttribute(
        "error",
        "Failed to load data"
      );

      this.#setLoadingState(false);
    } finally {
      if (this.#requestController === controller) {
        this.#requestController = null;
      }
    }
  }

  renderSearchResults(responseText) {
    const id =
      `PredictiveSearchResults-${getSectionId(this)}`;

    // Search inside this component, avoiding conflicts
    // if the theme renders several search instances.
    const targetElement = Array.from(
      this.querySelectorAll("[id]")
    ).find((element) => element.id === id);

    if (!targetElement) {
      console.error(
        `Element with id '${id}' not found.`
      );
      return;
    }

    const parser = new DOMParser();

    const parsedDoc = parser.parseFromString(
      responseText,
      "text/html"
    );

    let contentElement;

    if (this.isArabicLocale) {
      // Arabic results come from regular Shopify search.
      contentElement = parsedDoc.querySelector(
        "[data-arabic-search-results]"
      );
    } else {
      // English results use the original theme section.
      contentElement = parsedDoc.getElementById(id);
    }

    if (!contentElement) {
      console.error(
        "Search results container not found in response."
      );
      return;
    }

    this.refs.searchContent?.classList.remove(
      "hidden"
    );

    targetElement.innerHTML = contentElement.innerHTML;

    this.toggleSearchState(true);
  }

  #handleClickOutside(event) {
    const { target } = event;

    if (this.contains(target)) {
      clearTimeout(this.#focusoutTimeout);
      return;
    }

    if (
      this.isHeaderSearch &&
      !target.classList.contains("fixed-overlay") &&
      !target.closest(".header-section")
    ) {
      return;
    }

    setTimeout(() => {
      this.toggleSearchState(false);
    });
  }

  #handleFocusOut(event) {
    const { relatedTarget } = event;

    if (
      !relatedTarget ||
      this.contains(relatedTarget)
    ) {
      return;
    }

    if (
      this.#lastKeydown === "Tab" &&
      this.classList.contains(this.states.OPEN)
    ) {
      this.#focusoutTimeout = setTimeout(
        () => this.toggleSearchState(false),
        200
      );
    }
  }

  toggleSearchState(shouldOpen) {
    const isOpened = this.classList.contains(
      this.states.OPEN
    );

    if (isOpened === shouldOpen) {
      return;
    }

    if (this.#isLoading && !shouldOpen) {
      this.#cancelRequest();
    }

    this.classList.toggle(
      this.states.OPEN,
      shouldOpen
    );

    if (this.isHeaderSearch) {
      if (shouldOpen) {
        this.#cancelCloseAnimation();

        document.body.classList.add(
          this.states.SEARCH_OPEN
        );

        document.addEventListener(
          "click",
          this.clickOutsideHandler
        );

        document.addEventListener(
          "keydown",
          this.escKeyPressHandler
        );
      } else {
        this.#closeSearchWithAnimation();

        document.removeEventListener(
          "click",
          this.clickOutsideHandler
        );

        document.removeEventListener(
          "keydown",
          this.escKeyPressHandler
        );
      }
    }

    if (!shouldOpen && this.isEmptyRecommendations) {
      this.refs.searchContent?.classList.add(
        "hidden"
      );
    }
  }

  #getContentInner() {
    return this.refs.searchContent?.querySelector(
      ".search__content-inner"
    );
  }

  #cancelCloseAnimation() {
    if (!this.transitionEndHandler) {
      return;
    }

    const contentInner = this.#getContentInner();

    if (contentInner) {
      contentInner.removeEventListener(
        "transitionend",
        this.transitionEndHandler
      );
    }

    this.transitionEndHandler = null;
  }

  #closeSearchWithAnimation() {
    const contentInner = this.#getContentInner();

    if (!contentInner) {
      removeScrollLockClass(
        document.body,
        this.states.SEARCH_OPEN
      );
      return;
    }

    this.#cancelCloseAnimation();

    removeScrollLockClass(
      document.body,
      this.states.SEARCH_OPEN
    );

    this.transitionEndHandler = (event) => {
      if (
        event.target === contentInner &&
        event.propertyName === "transform"
      ) {
        this.transitionEndHandler = null;
      }
    };

    contentInner.addEventListener(
      "transitionend",
      this.transitionEndHandler,
      { once: true }
    );
  }

  #setLoadingState(isLoading) {
    this.#isLoading = isLoading;

    if (isLoading) {
      this.setAttribute("loading", "true");
    } else {
      this.removeAttribute("loading");
      this.setAttribute("results", "true");
    }
  }
}

customElements.define(
  "predictive-search",
  PredictiveSearch
);
